import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { LetsTalkForm } from "@/components/LetsTalkForm";
import { Phone, Mail, MapPin, Clock, Navigation, MessageSquare, Star, Quote, ShieldCheck, Wrench, MapPin as MapPinIcon, ArrowUp, Calendar } from "lucide-react";
import { useEffect, useState } from "react";
import { track } from "@/lib/analytics";
import ogImg from "@/assets/original/hero-shade.jpg";
import contactSignImg from "@/assets/contact-texas-made-sign.jpg";
import lobbyFabricSamplesImg from "@/assets/customer-lobby-fabric-samples.jpg";
import shopFrontImg from "@/assets/texas-made-shade-shop-seguin-tx.jpg";
import { useShopAddress, mapsEmbedUrl, mapsDirectionsUrl, mapsSearchUrl, formatFullAddress } from "@/lib/shop-address";
import { BusinessHours } from "@/components/BusinessHours";
import { DirectionsWidget } from "@/components/DirectionsWidget";

export const Route = createFileRoute("/contact")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Contact Awning Company in Seguin, TX | Texas Made Shade",
      description: "Local Seguin, TX awning company serving San Antonio, New Braunfels & South Texas. Custom awnings, patio covers, sail shades & marine canvas.",
      image: ogImg,
      socialTitle: "Contact Texas Made Shade — Seguin, TX Awning Company",
      socialDescription: "Family-owned Seguin, TX awning company. Custom awnings, patio covers, sail shades & marine canvas across South Texas & the Gulf Coast. Free quotes.",
    }),
  }),
  component: ContactPage,
});

function ContactPage() {
  const { address } = useShopAddress();
  const fullAddress = formatFullAddress(address);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="flex min-h-screen flex-col scroll-smooth">
      <SiteHeader />
      <main id="main-content" tabIndex={-1}>
        <section id="top" className="bg-navy-deep py-20 text-white scroll-mt-24">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <div className="font-display text-sm tracking-[0.3em]">★ ABOUT US · CONTACT US</div>
            <h1 className="mt-3 font-display text-5xl font-bold md:text-7xl">Contact Our Seguin, TX Awning Company</h1>
            <p className="mt-4 max-w-2xl text-lg text-white/80">Family-owned awning company in Seguin, Texas serving San Antonio, New Braunfels, Austin, the Hill Country, South Texas and the Gulf Coast. 30+ years of custom awnings, patio &amp; pergola covers, sail shades and marine canvas — built and installed by our crew.</p>

            {/* Prominent above-the-fold CTAs — Request an Estimate first, then Call & Text */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <a
                href="#lets-talk"
                data-cta-placement="contact_hero_request_estimate"
                
                className="inline-flex items-center justify-center gap-3 bg-red px-7 py-5 font-display text-xl font-extrabold uppercase tracking-wider text-white shadow-2xl ring-2 ring-white/30 transition hover:bg-red-deep hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.5)] active:scale-[0.98] sm:text-2xl"
              >
                <Wrench className="h-6 w-6 sm:h-7 sm:w-7" aria-hidden="true" />
                <span>Request an Estimate</span>
              </a>
              <a
                href="tel:+18303909588"
                data-cta-placement="contact_hero_cta"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/70 bg-white/5 px-6 py-5 font-display text-lg font-bold uppercase tracking-wider text-white transition hover:bg-white hover:text-navy-deep"
              >
                <Phone className="h-5 w-5" aria-hidden="true" />
                <span>Call +1 (830) 390-9588</span>
              </a>
              <a
                href="sms:+18303909588"
                onClick={() => track("sms_click", { target: "8303909588", metadata: { placement: "contact_hero_cta" } })}
                className="inline-flex items-center justify-center gap-2 border-2 border-white/70 bg-white/5 px-6 py-5 font-display text-lg font-bold uppercase tracking-wider text-white transition hover:bg-white hover:text-navy-deep"
              >
                <MessageSquare className="h-5 w-5" aria-hidden="true" />
                Text Us
              </a>
            </div>
            <p className="mt-3 text-sm text-white/70">Mon–Thu 8:30a–5p · Fri 8:30a–4p · Free quotes · No pressure.</p>

            <div className="mt-10 grid gap-4 md:grid-cols-4">
              <div className="border-l-2 border-white/30 bg-white/5 p-5"><Phone className="mb-2 h-5 w-5" /><div className="font-display text-sm uppercase tracking-wider">Phone</div><a href="tel:+18303909588" className="text-white/80">+1 (830) 390-9588</a></div>
              <div className="border-l-2 border-white/30 bg-white/5 p-5"><Mail className="mb-2 h-5 w-5" /><div className="font-display text-sm uppercase tracking-wider">Email</div><a href="mailto:office@texasmadeshade.com" className="break-all text-white/80">office@texasmadeshade.com</a></div>
              <div className="border-l-2 border-white/30 bg-white/5 p-5"><MapPin className="mb-2 h-5 w-5" /><div className="font-display text-sm uppercase tracking-wider">Shop</div><div className="text-sm text-white/80">{address.street}<br/>{address.city}, {address.state} {address.postal_code}</div></div>
              <div className="border-l-2 border-white/30 bg-white/5 p-5"><Clock className="mb-2 h-5 w-5" /><div className="font-display text-sm uppercase tracking-wider">Hours</div><div className="text-sm text-white/80">Mon–Thu 8:30a–5p<br/>Fri 8:30a–4p · Closed major holidays</div></div>
            </div>
          </div>
        </section>

        {/* ABOUT US */}
        <section aria-labelledby="about-us-heading" className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-5xl px-4 lg:px-8">
            <div className="font-display text-sm tracking-[0.3em] text-red">★ ABOUT US</div>
            <h2 id="about-us-heading" className="mt-3 font-display text-4xl font-bold text-navy-deep md:text-5xl">
              Family-Owned. Texas-Built. 30+ Years In The Shop.
            </h2>
            <div className="mt-8 space-y-5 text-lg leading-relaxed text-navy-deep/85">
              <figure className="mb-2 float-none w-full md:float-right md:ml-6 md:mb-3 md:w-[42%] lg:w-[46%]">
                <img
                  src={shopFrontImg}
                  alt="Texas Made Shade & Covers storefront in Seguin, TX with Boat Covers, Bimini Tops, Tarps and Outdoor Curtains signage"
                  className="h-auto w-full border-2 border-navy-deep shadow-bold"
                  width={1600}
                  height={2133}
                  loading="lazy"
                  decoding="async"
                />
                <figcaption className="mt-2 font-display text-xs uppercase tracking-[0.2em] text-navy-deep/60">
                  Our Seguin, TX shop — drop by anytime
                </figcaption>
              </figure>
              <p>
                Texas Made Shade &amp; Covers is a family-owned company based in Seguin, Texas, specializing in custom-built awnings, shade structures, and covers designed to withstand the demanding Texas climate.
              </p>
              <p>
                With over <strong>30 years of experience</strong> and more than <strong>1,000 completed projects</strong>, we take pride in building every product in-house — from welded aluminum frames to precision-patterned and sewn canvas — using high-quality materials like Sunbrella and marine-grade fabrics.
              </p>
              <p>
                Our team handles every step of the process, from initial measurements to final installation, ensuring consistent craftsmanship and accountability on every job.
              </p>
              <p>
                Serving customers across <strong>Central Texas, the Hill Country, the Gulf Coast, and beyond</strong>, we are committed to delivering durable, custom solutions for residential, commercial, and specialty applications — all backed by real people, a real shop, and long-term service you can count on.
              </p>
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              <div className="border-l-4 border-red bg-cream p-5">
                <div className="font-display text-3xl font-bold text-navy-deep">30+</div>
                <div className="mt-1 font-display text-xs uppercase tracking-[0.2em] text-navy-deep/70">Years of experience</div>
              </div>
              <div className="border-l-4 border-red bg-cream p-5">
                <div className="font-display text-3xl font-bold text-navy-deep">1,000+</div>
                <div className="mt-1 font-display text-xs uppercase tracking-[0.2em] text-navy-deep/70">Completed projects</div>
              </div>
              <div className="border-l-4 border-red bg-cream p-5">
                <div className="font-display text-3xl font-bold text-navy-deep">100%</div>
                <div className="mt-1 font-display text-xs uppercase tracking-[0.2em] text-navy-deep/70">Built in-house in Seguin, TX</div>
              </div>
            </div>
          </div>
        </section>

        {/* OUR FOUNDERS */}
        <section aria-labelledby="our-founders-heading" className="bg-navy-deep py-16 text-white md:py-20">
          <div className="mx-auto max-w-5xl px-4 lg:px-8">
            <div className="font-display text-sm tracking-[0.3em] text-red">★ OUR FOUNDERS</div>
            <h2 id="our-founders-heading" className="mt-3 font-display text-4xl font-bold md:text-5xl">
              John &amp; Marlana Muzny
            </h2>
            <div className="mt-8 space-y-5 text-lg leading-relaxed text-white/85">
              <p>
                Almost 30 years of experience, John and Marlana Muzny began their career at an awning company locally in Seguin, TX. They learned everything they could about the business and made long-lasting friendships with suppliers and customers.
              </p>
              <p>
                When that awning company locally decided to close their doors, the Muznys realized there was still a great need for awnings and covers in South Texas — so they set out on their own adventure and opened Texas Made Shade and Covers.
              </p>
              <p>
                This adventure is more than a business to them; it has become an entire family's legacy, even having their two sons help with welding and fabrication. They pride themselves on their work ethic, but in their spare time enjoy fishing and spending time with friends and family.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-cream py-16">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-2 lg:items-stretch">
              <div className="flex flex-col justify-center">
                <div className="font-display text-sm tracking-[0.3em] text-rust">★ FIND US</div>
                <h2 className="mt-3 font-display text-3xl font-bold text-navy-deep md:text-4xl">Get Driving Directions</h2>
                <p className="mt-4 text-navy-deep/80">
                  Visit our shop at <strong>{fullAddress}</strong>. Tap the button below to open Google Maps and get turn-by-turn directions from your address.
                </p>
                <div className="mt-6">
                  <DirectionsWidget />
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  <a
                    href={mapsDirectionsUrl(address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      track("directions_click", {
                        target: "get_directions_button",
                        metadata: { placement: "contact_find_us", action: "directions" },
                      })
                    }
                    className="inline-flex items-center gap-2 bg-navy-deep px-6 py-3 font-display text-sm uppercase tracking-wider text-white transition hover:bg-navy-deep/90"
                  >
                    <Navigation className="h-4 w-4" aria-hidden="true" />
                    Get Directions
                  </a>
                  <a
                    href={mapsSearchUrl(address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      track("directions_click", {
                        target: "view_on_google_maps_button",
                        metadata: { placement: "contact_find_us", action: "view_map" },
                      })
                    }
                    className="inline-flex items-center gap-2 border-2 border-navy-deep px-6 py-3 font-display text-sm uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
                  >
                    <MapPin className="h-4 w-4" aria-hidden="true" />
                    View on Google Maps
                  </a>
                </div>
              </div>
              <div className="overflow-hidden border-2 border-navy-deep/10 shadow-card">
                <iframe
                  title={`${address.shop_name} location map`}
                  src={mapsEmbedUrl(address)}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="h-[360px] w-full lg:h-full"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </section>
        <section className="bg-white py-16">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
              <div className="overflow-hidden border-2 border-navy-deep/10 shadow-card">
                <img
                  src={contactSignImg}
                  alt="Texas Made Shade & Covers sign"
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
              <div>
                <div className="font-display text-sm tracking-[0.3em] text-rust">★ SHOP PHOTO</div>
                <h2 className="mt-3 font-display text-3xl font-bold text-navy-deep md:text-4xl">Texas Made Shade & Covers</h2>
                <p className="mt-4 text-navy-deep/80">
                  We're a true brick-and-mortar shop — not a home-based operation. When you work with Texas Made Shade & Covers, you're working with a team that designs, welds, sews, and installs every product under one roof in Seguin, TX.
                </p>
                <p className="mt-3 text-navy-deep/80">
                  Many companies in this industry — even some of the largest names — operate out of residential garages or subcontract every step of the build. That often means longer lead times, less accountability, and products that were never touched by the people selling them to you.
                </p>
                <p className="mt-3 text-navy-deep/80">
                  Our storefront is open to the public. You're welcome to stop by, meet the crew, browse fabric samples, and see the work in progress. We believe knowing who builds your shade matters — and we're proud to stand behind every weld, every stitch, and every install we do.
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <a
                    href="tel:+18303909588"
                    className="inline-flex items-center gap-2 rounded-md bg-rust px-6 py-3 font-display text-sm font-bold tracking-wide text-white shadow-card transition hover:bg-red"
                    onClick={() => track("cta_click", { metadata: { location: "shop_photo", label: "Request Free Quote" } })}
                  >
                    <Phone className="h-4 w-4" />
                    Request a Free Quote
                  </a>
                  <a
                    href="#lets-talk"
                    className="inline-flex items-center gap-2 rounded-md border-2 border-navy-deep px-6 py-3 font-display text-sm font-bold tracking-wide text-navy-deep transition hover:bg-navy-deep hover:text-white"
                    onClick={() => track("cta_click", { metadata: { location: "shop_photo", label: "Schedule Shop Visit" } })}
                  >
                    <Calendar className="h-4 w-4" />
                    Schedule a Shop Visit
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="bg-cream py-16">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <div className="mb-10 text-center">
              <div className="font-display text-sm tracking-[0.3em] text-rust">★ CUSTOMER WORDS</div>
              <h2 className="mt-3 font-display text-3xl font-bold text-navy-deep md:text-4xl">What Our Customers Say</h2>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              <blockquote className="border-l-4 border-red bg-white p-6 shadow-card">
                <Quote className="mb-3 h-6 w-6 text-red/60" aria-hidden="true" />
                <p className="text-navy-deep/85 italic">
                  "I drove out to Seguin specifically because I wanted to see the shop and meet the people who would be building my patio cover. You can tell this is a real business — welding rigs, fabric rolls, and a crew that knows their craft."
                </p>
                <footer className="mt-4 font-display text-sm font-bold text-navy-deep">— Mark R., San Antonio</footer>
              </blockquote>
              <blockquote className="border-l-4 border-red bg-white p-6 shadow-card">
                <Quote className="mb-3 h-6 w-6 text-red/60" aria-hidden="true" />
                <p className="text-navy-deep/85 italic">
                  "We visited three different companies before choosing Texas Made Shade. The difference was night and day — here we walked into an actual storefront, looked at samples in person, and talked directly with the fabricator. No middlemen."
                </p>
                <footer className="mt-4 font-display text-sm font-bold text-navy-deep">— Jennifer L., New Braunfels</footer>
              </blockquote>
              <blockquote className="border-l-4 border-red bg-white p-6 shadow-card">
                <Quote className="mb-3 h-6 w-6 text-red/60" aria-hidden="true" />
                <p className="text-navy-deep/85 italic">
                  "After a bad experience with a garage-based outfit that outsourced everything, I was relieved to find a true brick-and-mortar shop. They built our sail shade frame in-house and stood behind the install 100%."
                </p>
                <footer className="mt-4 font-display text-sm font-bold text-navy-deep">— David &amp; Lisa K., Seguin</footer>
              </blockquote>
            </div>
          </div>
        </section>
        <section className="bg-white py-16">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
              <div>
                <div className="font-display text-sm tracking-[0.3em] text-rust">★ STOP BY THE SHOP</div>
                <h2 className="mt-3 font-display text-3xl font-bold text-navy-deep md:text-4xl">Sit Down, Flip Through Fabrics, Talk It Out</h2>
                <p className="mt-4 text-navy-deep/80">
                  When you visit our Seguin shop, you don't get a sales counter — you get a seat at the table. Spread out the Sunbrella® and Serge Ferrari® sample books, hold the colors up to the light, and walk through your project with someone who actually builds the work.
                </p>
                <p className="mt-3 text-navy-deep/80">
                  Bring photos, dimensions, or just an idea. We'll talk fabric, frame, mounting, and price — no pressure, no hard sell, and the coffee's usually on.
                </p>
              </div>
              <div className="overflow-hidden border-2 border-navy-deep/10 shadow-card">
                <img
                  src={lobbyFabricSamplesImg}
                  alt="Customer consultation table inside the Texas Made Shade & Covers Seguin showroom — Sunbrella and Serge Ferrari fabric sample books, a written proposal, and a candle laid out on a round wood-top table with a leather barstool"
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>
        <BusinessHours />
        <section aria-labelledby="quick-contact-heading" className="bg-navy-deep py-14 text-white">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <div className="flex flex-col items-start gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-2xl">
                <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Skip The Form</div>
                <h2 id="quick-contact-heading" className="mt-2 font-display text-3xl font-bold leading-tight md:text-4xl">
                  Prefer To Talk Now? Call Or Email Us Direct.
                </h2>
                <p className="mt-3 text-base text-white/85">
                  Real human in Seguin, TX — usually same-business-day response. No call centers, no bots.
                </p>
              </div>
              <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap lg:w-auto lg:shrink-0">
                <a
                  href="tel:+18303909588"
                  data-cta-placement="contact_above_form_cta"
                  className="inline-flex items-center justify-center gap-2 bg-red px-7 py-4 font-display text-base font-extrabold uppercase tracking-wider text-white shadow-2xl ring-2 ring-white/30 transition hover:bg-red-deep active:scale-[0.98] md:text-lg"
                >
                  <Phone className="h-5 w-5" aria-hidden="true" />
                  Call +1 (830) 390-9588
                </a>
                <a
                  href="mailto:office@texasmadeshade.com?subject=Quote%20Request%20—%20Texas%20Made%20Shade"
                  onClick={() => track("email_click", { target: "office@texasmadeshade.com", metadata: { placement: "contact_above_form_cta" } })}
                  className="inline-flex items-center justify-center gap-2 bg-white px-7 py-4 font-display text-base font-extrabold uppercase tracking-wider text-navy-deep shadow-xl transition hover:bg-cream active:scale-[0.98] md:text-lg"
                >
                  <Mail className="h-5 w-5" aria-hidden="true" />
                  Email Us
                </a>
                <a
                  href="sms:+18303909588"
                  onClick={() => track("sms_click", { target: "8303909588", metadata: { placement: "contact_above_form_cta" } })}
                  className="inline-flex items-center justify-center gap-2 border-2 border-white/70 px-6 py-4 font-display text-base font-bold uppercase tracking-wider text-white transition hover:bg-white hover:text-navy-deep md:text-lg"
                >
                  <MessageSquare className="h-5 w-5" aria-hidden="true" />
                  Text Us
                </a>
              </div>
            </div>
          </div>
        </section>
        <section aria-labelledby="contact-trust-heading" className="bg-cream py-16">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <div className="text-center">
              <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Trusted Across Texas</div>
              <h2 id="contact-trust-heading" className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
                Real Texas Customers. Real Shop. Real Crew.
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-base text-navy-deep/75">
                Before you hit send — here's what folks across South Texas, the Hill Country, and the Gulf Coast say about working with us.
              </p>
            </div>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {[
                {
                  quote: "Showed up when they said, measured everything twice, and the awning has held up through two summers and a hailstorm. These guys are the real deal.",
                  author: "Marcus R.",
                  role: "Homeowner",
                  location: "Seguin, TX",
                },
                {
                  quote: "We've used Texas Made Shade for two restaurant patios now. Welded frames, custom Sunbrella, perfect install. Best canvas shop in South Texas, hands down.",
                  author: "Lauren P.",
                  role: "Property Manager",
                  location: "San Antonio, TX",
                },
                {
                  quote: "Patterned my T-top at the dock and had the new canvas back on in a week. Stitching is tight, hardware is marine grade. Couldn't ask for better.",
                  author: "Capt. Dave H.",
                  role: "Offshore Charter",
                  location: "Port Aransas, TX",
                },
              ].map((t) => (
                <figure key={t.author} className="relative flex flex-col border-l-4 border-red bg-white p-6 shadow-card">
                  <Quote className="absolute right-4 top-4 h-8 w-8 text-red/15" aria-hidden="true" />
                  <div role="img" className="flex gap-0.5" aria-label="5 out of 5 stars">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-red text-red" aria-hidden="true" />
                    ))}
                  </div>
                  <blockquote className="mt-3 text-base leading-relaxed text-navy-deep">“{t.quote}”</blockquote>
                  <figcaption className="mt-5 border-t border-navy-deep/10 pt-4">
                    <div className="font-display text-sm font-bold uppercase tracking-wider text-navy-deep">{t.author}</div>
                    <div className="mt-1 text-xs text-navy-deep/70">{t.role} · {t.location}</div>
                  </figcaption>
                </figure>
              ))}
            </div>
            <ul className="mt-10 grid gap-4 sm:grid-cols-3">
              {[
                { icon: Wrench, label: "Built In Our Seguin Shop", note: "TIG/MIG welding + industrial canvas line, on-site." },
                { icon: ShieldCheck, label: "Free, No-Pressure Quotes", note: "Same-business-day response from a real human." },
                { icon: MapPinIcon, label: "Serving All Of Texas", note: "Hill Country, Gulf Coast, San Antonio & Austin metros." },
              ].map((b) => (
                <li key={b.label} className="flex items-start gap-3 border border-navy-deep/15 bg-white p-4">
                  <b.icon className="mt-0.5 h-5 w-5 shrink-0 text-red" aria-hidden="true" />
                  <div>
                    <div className="font-display text-sm font-bold uppercase tracking-wider text-navy-deep">{b.label}</div>
                    <div className="mt-1 text-xs text-navy-deep/70">{b.note}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
        <div className="scroll-mt-24"><LetsTalkForm /></div>
        <ContactFaqs />

        {/* INTERNAL LINKS — relevant awning service & local pages for SEO */}
        <section aria-labelledby="contact-services-heading" className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-5xl px-4 lg:px-8">
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Explore Our Work</div>
            <h2 id="contact-services-heading" className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
              Custom Awning &amp; Shade Services from Our Seguin, TX Shop
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-navy-deep/80">
              Browse the most-requested services from our{" "}
              <Link to="/seguin" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">Seguin, TX awning shop</Link>{" "}
              — built in-house and installed across{" "}
              <Link to="/san-antonio" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">San Antonio</Link>,{" "}
              <Link to="/new-braunfels" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">New Braunfels</Link>,{" "}
              <Link to="/austin" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">Austin</Link>,{" "}
              <Link to="/corpus-christi" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">Corpus Christi</Link>{" "}
              and the broader{" "}
              <Link to="/hill-country" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">Hill Country</Link>,{" "}
              <Link to="/central-texas" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">Central Texas</Link>{" "}
              and{" "}
              <Link to="/gulf-coast" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">Gulf Coast</Link>{" "}
              service area.
            </p>

            <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[
                { to: "/awnings", label: "Custom Awnings" },
                { to: "/commercial-awnings", label: "Commercial Storefront Awnings" },
                { to: "/residential-awnings", label: "Residential Awnings" },
                { to: "/metal-awnings", label: "Metal Awnings" },
                { to: "/fabric-awnings", label: "Fabric Awnings" },
                { to: "/pergola-covers", label: "Patio, Gazebo & Pergola Covers" },
                { to: "/sail-shades", label: "Sail Shades" },
                { to: "/curtains", label: "Outdoor Curtains & Drop Panels" },
                { to: "/boat-covers", label: "Boat Covers & Marine Canvas" },
                { to: "/stock-trailer-covers", label: "Stock Trailer Covers" },
                { to: "/custom-fabrication", label: "Welding & Custom Fabrication" },
              ].map((s) => (
                <li key={s.to}>
                  <Link
                    to={s.to}
                    className="flex items-center justify-between gap-3 border-l-4 border-red bg-cream px-4 py-3 font-display text-sm font-bold uppercase tracking-wide text-navy-deep transition hover:bg-navy-deep hover:text-white"
                  >
                    <span>{s.label}</span>
                    <span aria-hidden="true">→</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Post-form quick-return CTA — visible after the form so users always
            have an in-flow way back to the top without scrolling manually. */}
        <section aria-label="Return to top of page" className="bg-cream py-10">
          <div className="mx-auto flex max-w-4xl items-center justify-center px-4 lg:px-8">
            <a
              href="#top"
              onClick={(e) => { e.preventDefault(); scrollToTop(); }}
              className="inline-flex items-center justify-center gap-2 border-2 border-navy-deep bg-white px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
            >
              <ArrowUp className="h-4 w-4" aria-hidden="true" /> Back to top
            </a>
          </div>
        </section>
      </main>

      {/* Floating Back-to-Top — appears after the user scrolls past the hero */}
      <button
        type="button"
        onClick={scrollToTop}
        aria-label="Back to top"
        className={`fixed bottom-6 left-6 z-40 inline-flex h-12 w-12 items-center justify-center rounded-full bg-navy-deep text-white shadow-lg ring-2 ring-white/40 transition-all duration-200 hover:bg-red focus:outline-none focus-visible:ring-4 focus-visible:ring-red ${showTop ? "opacity-100" : "pointer-events-none opacity-0"}`}
      >
        <ArrowUp className="h-5 w-5" aria-hidden="true" />
      </button>

      <SiteFooter />
    </div>
  );
}

const CONTACT_FAQS = [
  {
    q: "How quickly will you respond to my quote request?",
    a: "Most quote requests get a quick reply from a real person in our Seguin shop. If you submit late in the day or on a weekend, expect a response by the next business morning. Phone calls and texts to +1 (830) 390-9588 during business hours (Mon–Thu 8:30a–5p, Fri 8:30a–4p) are usually answered live.",
  },
  {
    q: "How long does it take to get a custom awning or cover built and installed?",
    a: "Typical turnaround from approved estimate to install is 2–4 weeks for residential awnings, 3–6 weeks for commercial storefront awnings and welded sail-shade structures, and 1–3 weeks for re-covers and canvas-only jobs. Custom welding-only fabrication varies — we'll give you a firm timeline with your written estimate.",
  },
  {
    q: "Do you measure on-site, or do I need to send dimensions?",
    a: "Both work. For most jobs in Central Texas, South Texas, the Hill Country, and the Gulf Coast we send a crew to pattern and measure on-site (free for projects in our service area). For smaller jobs, recovers, and out-of-area projects, you can send rough dimensions and a few photos and we'll quote from those. We confirm everything before fabrication begins.",
  },
  {
    q: "What's included in the free estimate?",
    a: "Your written estimate spells out materials (Sunbrella, Tempotest, vinyl, aluminum or steel gauge), frame and hardware specs, color or finish selection, fabrication time, install scope, and total price — no hidden line items. Estimates are good for 30 days and there's no obligation.",
  },
  {
    q: "What information should I include with my request?",
    a: "Helpful details: project type (awning, cover, sail shade, etc.), location/city, rough dimensions or sq. ft., fabric color or material preference, any photos of the space, and your timeline. Don't worry about getting it perfect — we'll follow up with sizing questions before quoting.",
  },
  {
    q: "Do you charge for estimates or site visits?",
    a: "No. Quotes and on-site measurements are free across our standard service area (San Antonio, New Braunfels, Seguin, Austin, Boerne, Kerrville, Fredericksburg, Corpus Christi, and Greater Houston). For projects outside that area, we'll let you know upfront if a travel fee applies.",
  },
];

function ContactFaqs() {
  return (
    <section aria-labelledby="contact-faq-heading" className="bg-white py-20">
      <div className="mx-auto max-w-4xl px-4 lg:px-8">
        <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Common Questions</div>
        <h2 id="contact-faq-heading" className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl">
          Before You Reach Out
        </h2>
        <p className="mt-4 max-w-2xl text-base text-navy-deep/75">
          Quick answers on turnaround, measurements, and what's in your free written estimate. Don't see your question? Call <a href="tel:+18303909588" className="font-semibold text-navy-deep underline">+1 (830) 390-9588</a> or email <a href="mailto:office@texasmadeshade.com" className="font-semibold text-navy-deep underline">office@texasmadeshade.com</a>.
        </p>
        <div className="mt-10 divide-y divide-navy-deep/15 border-y border-navy-deep/15">
          {CONTACT_FAQS.map((f) => (
            <details key={f.q} className="group py-6">
              <summary className="flex cursor-pointer list-none items-start justify-between gap-6 font-display text-lg font-bold text-navy-deep">
                {f.q}
                <span className="font-display text-2xl leading-none text-red transition group-open:rotate-45" aria-hidden="true">+</span>
              </summary>
              <p className="mt-4 text-base leading-relaxed text-navy-deep/80">{f.a}</p>
            </details>
          ))}
        </div>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: CONTACT_FAQS.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          }}
        />
      </div>
    </section>
  );
}

